/* eslint-disable no-empty */
import pako from 'pako';
import { useAuthStore } from './store/auth';
import { getOrCreateClientRunId } from './lib/clientRun';
import type {
  ArgusCollectionDraftV1,
  ArgusCaptureQcV1,
  ArgusDiagnosisStatus,
  ArgusOfficialDiagnosisSummary,
  ArgusParticipantSummary,
  ArgusVideoArtifactSummary,
  EyeTraxSample,
  TimelineSeg,
  GazeTestResult,
  GazeTestResultDetail,
} from './types';

const API_BASE = ((import.meta.env.VITE_API_BASE as string | undefined) ?? '/api').replace(
  /\/$/,
  '',
);

type TaskResult = {
  task: string;
  label: string;
  confidence: number;
  prob?: number;
  threshold?: number;
};

const filterTasks = (tasks?: TaskResult[] | undefined): TaskResult[] | undefined =>
  Array.isArray(tasks)
    ? tasks.filter((task) => String(task.task).toUpperCase() !== 'CDR')
    : undefined;

function coerceCaptureQc(value: unknown): ArgusCaptureQcV1 | null {
  if (!value || typeof value !== 'object') return null;
  const record = value as Record<string, unknown>;
  const totalFrames = Number(record.totalFrames ?? 0);
  const facePresentFrames = Number(record.facePresentFrames ?? 0);
  const facePresentRatio = Number(record.facePresentRatio ?? 0);
  const longestNoFaceMs = Number(record.longestNoFaceMs ?? 0);
  const segmentsRaw = Array.isArray(record.segments) ? record.segments : [];
  return {
    version: 1,
    passed: Boolean(record.passed),
    reason: typeof record.reason === 'string' ? record.reason : null,
    totalFrames: Number.isFinite(totalFrames) ? totalFrames : 0,
    facePresentFrames: Number.isFinite(facePresentFrames) ? facePresentFrames : 0,
    facePresentRatio: Number.isFinite(facePresentRatio) ? facePresentRatio : 0,
    multiFaceFrames: Number(record.multiFaceFrames ?? 0) || 0,
    longestNoFaceMs: Number.isFinite(longestNoFaceMs) ? longestNoFaceMs : 0,
    minFacePresentRatio: Number(record.minFacePresentRatio ?? 0.85) || 0.85,
    maxAllowedNoFaceMs: Number(record.maxAllowedNoFaceMs ?? 10000) || 10000,
    minSegmentFaceRatio: Number(record.minSegmentFaceRatio ?? 0.6) || 0.6,
    maxFailedSegmentRatio: Number(record.maxFailedSegmentRatio ?? 0.2) || 0.2,
    failedSegmentCount: Number(record.failedSegmentCount ?? 0) || 0,
    segmentCount: Number(record.segmentCount ?? segmentsRaw.length) || 0,
    segments: segmentsRaw
      .map((segment) => {
        const item = (segment as Record<string, unknown>) ?? {};
        return {
          mediaIndex: Number(item.mediaIndex),
          totalFrames: Number(item.totalFrames ?? 0),
          facePresentFrames: Number(item.facePresentFrames ?? 0),
          coverage: Number(item.coverage ?? 0),
        };
      })
      .filter((segment) => Number.isFinite(segment.mediaIndex)),
  };
}

type UploadOpts = {
  targetMaxBytes: number; // e.g. 1_500_000
  seedChunkSize: number; // e.g. 80
  onProgress?: (info: { sentChunks: number; totalChunks: number; bytes: number }) => void;
};

type ExtraMeta = {
  static?: { name?: string; age: number; sex: 'M' | 'F' | string; education: number };
  timeline?: TimelineSeg[];
};

export type UploadSamplePayload = {
  ok: true;
  mediaIndex: number;
  landmarks: EyeTraxSample['landmarks'];
  timestamp: number;
};

type UploadSampleInput = EyeTraxSample | UploadSamplePayload;

type LoginResponse = { detail: string; username?: string };

type FetchInit = RequestInit & { headers?: HeadersInit };

export type VideoSubmitProgress = {
  loadedBytes: number;
  totalBytes: number;
  percent: number | null;
  phase: 'uploading' | 'processing';
};

const VIDEO_SUBMIT_TIMEOUT_MS = 15 * 60 * 1000;
const VIDEO_UPLOAD_STALL_TIMEOUT_MS = 90 * 1000;
const VIDEO_UPLOAD_MAX_ATTEMPTS = 3;
const VIDEO_UPLOAD_RETRY_BASE_DELAY_MS = 1500;
const VIDEO_UPLOAD_RETRY_MAX_DELAY_MS = 10_000;
const VIDEO_UPLOAD_RETRYABLE_STATUSES = new Set([408, 425, 429, 500, 502, 503, 504]);

const delay = (ms: number): Promise<void> => new Promise((resolve) => window.setTimeout(resolve, ms));

function retryDelayMs(attempt: number): number {
  const exponential = VIDEO_UPLOAD_RETRY_BASE_DELAY_MS * 2 ** Math.max(0, attempt - 1);
  const jitter = Math.floor(Math.random() * 250);
  return Math.min(VIDEO_UPLOAD_RETRY_MAX_DELAY_MS, exponential + jitter);
}

function isRetryableUploadStatus(statusCode: number): boolean {
  return VIDEO_UPLOAD_RETRYABLE_STATUSES.has(statusCode);
}

function formatUploadError(status: number, text: string): string {
  const snippet = text.trim().slice(0, 200);
  if (status === 413) {
    return '视频文件过大，服务器已拒绝接收。请重新开始采集，系统会使用更小的视频码率。';
  }
  if (status === 401 || status === 403) {
    return '登录状态已过期，请重新登录后再提交。';
  }
  if (status === 502 || status === 503 || status === 504) {
    return '服务器暂时无法保存视频，请稍后点击重试。';
  }
  return `视频提交失败 (${status})${snippet ? ` – ${snippet}` : ''}`;
}

function cloneBody(body: BodyInit | null | undefined): BodyInit | null | undefined {
  if (body instanceof Uint8Array) return body.slice();
  if (body instanceof ArrayBuffer) return body.slice(0);
  return body ?? undefined;
}

function buildRequestInit(init: FetchInit = {}): RequestInit {
  const headers = new Headers(init.headers ?? undefined);
  const cloned: RequestInit = {
    ...init,
    headers,
    body: cloneBody(init.body ?? undefined),
    credentials: 'include',
  };
  return cloned;
}

async function refreshSession(): Promise<boolean> {
  try {
    const res = await fetch(`${API_BASE}/token/refresh/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: '{}',
    });
    if (!res.ok) return false;
    return true;
  } catch {
    return false;
  }
}

async function authedFetch(path: string, init: FetchInit = {}, tried = false): Promise<Response> {
  const requestInit = buildRequestInit(init);
  const res = await fetch(`${API_BASE}${path}`, requestInit);
  if (res.status !== 401) return res;
  if (tried) {
    useAuthStore.getState().clearAuth();
    return res;
  }
  const refreshed = await refreshSession();
  if (!refreshed) {
    useAuthStore.getState().clearAuth();
    return res;
  }
  const retry = await fetch(`${API_BASE}${path}`, buildRequestInit(init));
  if (retry.status === 401) {
    useAuthStore.getState().clearAuth();
  }
  return retry;
}

async function authedFormUpload(
  path: string,
  body: FormData,
  onProgress?: (info: VideoSubmitProgress) => void,
): Promise<{ status: number; text: string }> {
  const totalBytes = (() => {
    const video = body.get('video');
    return video instanceof Blob ? video.size : 0;
  })();

  const sendOnce = () =>
    new Promise<{ status: number; text: string }>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      let markedProcessing = false;
      let settled = false;
      let stallTimer: number | null = null;

      const clearStallTimer = () => {
        if (stallTimer !== null) {
          window.clearTimeout(stallTimer);
          stallTimer = null;
        }
      };

      const rejectOnce = (error: Error) => {
        if (settled) return;
        settled = true;
        clearStallTimer();
        reject(error);
      };

      const resolveOnce = (value: { status: number; text: string }) => {
        if (settled) return;
        settled = true;
        clearStallTimer();
        resolve(value);
      };

      const armStallTimer = () => {
        clearStallTimer();
        stallTimer = window.setTimeout(() => {
          if (settled || markedProcessing) return;
          rejectOnce(new Error('视频上传长时间没有进度，请检查网络后点击重试。'));
          try {
            xhr.abort();
          } catch {
            // ignore abort failures
          }
        }, VIDEO_UPLOAD_STALL_TIMEOUT_MS);
      };

      const emit = (info: VideoSubmitProgress) => {
        try {
          onProgress?.(info);
        } catch {
          // ignore progress handler errors
        }
      };

      const markProcessing = () => {
        if (markedProcessing) return;
        markedProcessing = true;
        emit({
          loadedBytes: totalBytes,
          totalBytes,
          percent: totalBytes > 0 ? 100 : null,
          phase: 'processing',
        });
        clearStallTimer();
      };

      xhr.open('POST', `${API_BASE}${path}`, true);
      xhr.withCredentials = true;
      xhr.timeout = VIDEO_SUBMIT_TIMEOUT_MS;

      if (totalBytes > 0) {
        emit({ loadedBytes: 0, totalBytes, percent: 0, phase: 'uploading' });
      }

      xhr.upload.addEventListener('progress', (event) => {
        armStallTimer();
        const resolvedTotal = event.lengthComputable && event.total > 0 ? event.total : totalBytes;
        const resolvedLoaded = Math.max(0, Math.min(event.loaded, resolvedTotal || totalBytes));
        emit({
          loadedBytes: resolvedLoaded,
          totalBytes: resolvedTotal,
          percent: resolvedTotal > 0 ? (resolvedLoaded / resolvedTotal) * 100 : null,
          phase: 'uploading',
        });
      });

      xhr.upload.addEventListener('load', markProcessing);
      xhr.upload.addEventListener('loadend', markProcessing);

      xhr.onload = () => {
        markProcessing();
        resolveOnce({ status: xhr.status, text: xhr.responseText ?? '' });
      };
      xhr.onerror = () => rejectOnce(new Error('视频上传失败：网络连接中断，请点击重试。'));
      xhr.onabort = () => rejectOnce(new Error('视频上传已中断，请点击重试。'));
      xhr.ontimeout = () => rejectOnce(new Error('视频上传超时，请检查网络后点击重试。'));

      try {
        armStallTimer();
        xhr.send(body);
      } catch (error) {
        rejectOnce(error instanceof Error ? error : new Error(String(error)));
      }
    });

  const sendWithAuthRefresh = async () => {
    const res = await sendOnce();
    if (res.status !== 401) return res;
    const refreshed = await refreshSession();
    if (!refreshed) return res;
    return sendOnce();
  };

  let lastError: unknown = null;
  for (let attempt = 1; attempt <= VIDEO_UPLOAD_MAX_ATTEMPTS; attempt += 1) {
    try {
      const res = await sendWithAuthRefresh();
      if (!isRetryableUploadStatus(res.status) || attempt >= VIDEO_UPLOAD_MAX_ATTEMPTS) {
        return res;
      }
      lastError = new Error(`HTTP ${res.status}`);
    } catch (error) {
      lastError = error;
      if (attempt >= VIDEO_UPLOAD_MAX_ATTEMPTS) {
        throw error;
      }
    }
    await delay(retryDelayMs(attempt));
  }

  throw lastError instanceof Error ? lastError : new Error('视频上传失败，请点击重试。');
}

async function downloadResponseToFile(response: Response, fallbackName: string): Promise<void> {
  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  const disposition = response.headers.get('Content-Disposition') || '';
  const match = /filename="?([^";]+)"?/i.exec(disposition);
  anchor.href = url;
  anchor.download = match?.[1] ?? fallbackName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.URL.revokeObjectURL(url);
}

function gzipJson(obj: unknown): Uint8Array {
  const raw = JSON.stringify(obj);
  return pako.gzip(raw);
}

const coerceFiniteNumber = (value: unknown, fallback = 0): number => {
  const numeric = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
};

const coerceInt = (value: unknown, fallback = 0): number =>
  Math.trunc(coerceFiniteNumber(value, fallback));

const coerceNullableNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const numeric = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

const coerceNullableString = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

const coerceDiagnosisStatus = (value: unknown): ArgusDiagnosisStatus | null => {
  return value === 'dementia' ||
    value === 'mci' ||
    value === 'cognitively_normal' ||
    value === 'subjective_cognitive_decline' ||
    value === 'not_yet_confirmed'
    ? value
    : null;
};

const coerceOfficialDiagnosis = (value: unknown): ArgusOfficialDiagnosisSummary | null => {
  if (!value || typeof value !== 'object') return null;
  const raw = value as Record<string, unknown>;
  const status = coerceDiagnosisStatus(raw.status);
  if (!status) return null;
  const id = coerceNullableNumber(raw.id);
  return {
    ...(id !== null ? { id } : {}),
    status,
    diagnosedOn: coerceNullableString(raw.diagnosedOn),
    clinicianName: coerceNullableString(raw.clinicianName),
    institution: coerceNullableString(raw.institution),
    notes: coerceNullableString(raw.notes),
    isCurrent: typeof raw.isCurrent === 'boolean' ? raw.isCurrent : undefined,
  };
};

const coerceParticipantSummary = (value: unknown): ArgusParticipantSummary | null => {
  if (!value || typeof value !== 'object') return null;
  const raw = value as Record<string, unknown>;
  const id = coerceNullableNumber(raw.id);
  const clinicParticipantId = coerceNullableString(raw.clinicParticipantId);
  if (id === null || !clinicParticipantId) return null;
  return {
    id,
    clinicParticipantId,
    name: coerceNullableString(raw.name),
  };
};

const coerceVideoArtifact = (value: unknown): ArgusVideoArtifactSummary | null => {
  if (!value || typeof value !== 'object') return null;
  const raw = value as Record<string, unknown>;
  return {
    filename: coerceNullableString(raw.filename),
    mimeType: coerceNullableString(raw.mimeType),
    sizeBytes: coerceNullableNumber(raw.sizeBytes),
    sha256: coerceNullableString(raw.sha256),
  };
};

export function normalizeSamplesForUpload(samples: UploadSampleInput[]): UploadSamplePayload[] {
  return samples.map((sample) => {
    const candidate = sample as Partial<EyeTraxSample> & Partial<UploadSamplePayload>;
    const landmarks = Array.isArray(candidate.landmarks)
      ? (candidate.landmarks as EyeTraxSample['landmarks'])
      : [];
    return {
      ok: true as const,
      mediaIndex: coerceInt(candidate.mediaIndex, 0),
      landmarks,
      timestamp: coerceFiniteNumber(candidate.timestamp, 0),
    };
  });
}

// ===== Auth API =====
export async function login(username: string, password: string): Promise<{ username: string }> {
  const res = await fetch(`${API_BASE}/token/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ username, password }),
  });
  if (!res.ok) {
    const t = await res.text();
    throw new Error(`登录失败 (${res.status}) – ${t.slice(0, 200)}`);
  }
  const data = (await res.json()) as LoginResponse;
  if (!data?.username) throw new Error('登录响应缺少用户名');
  return { username: data.username };
}

export async function logout(): Promise<void> {
  const res = await fetch(`${API_BASE}/logout/`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok && res.status !== 401) {
    const t = await res.text();
    throw new Error(`退出登录失败 (${res.status}) – ${t.slice(0, 200)}`);
  }
}

export async function registerUser(
  username: string,
  email: string,
  password: string,
): Promise<string> {
  const res = await fetch(`${API_BASE}/register/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ username, email, password }),
  });
  if (!res.ok) {
    const t = await res.text();
    throw new Error(`注册失败 (${res.status}) – ${t.slice(0, 200)}`);
  }
  try {
    const data = (await res.json()) as { detail?: string };
    return data?.detail ?? '激活邮件已发送，请查收邮箱。';
  } catch {
    return '激活邮件已发送，请查收邮箱。';
  }
}

// ===== App-specific API =====
export async function fetchGazeResults(): Promise<GazeTestResult[]> {
  const res = await authedFetch('/gaze-test-results/', { method: 'GET' });
  if (!res.ok) {
    const t = await res.text();
    throw new Error(`获取结果失败 (${res.status}) – ${t.slice(0, 200)}`);
  }
  const data = await res.json();
  if (!Array.isArray(data)) throw new Error('期望 gaze-test-results 返回数组');
  return data.map((item) => ({
    id: Number((item as { id?: unknown }).id),
    status: String((item as { status?: unknown }).status ?? ''),
    label: String((item as { label?: unknown }).label ?? ''),
    confidence: coerceNullableNumber((item as { confidence?: unknown }).confidence),
    timestamp: String((item as { timestamp?: unknown }).timestamp ?? ''),
    name: (() => {
      const raw = (item as { name?: unknown }).name;
      if (typeof raw !== 'string') return undefined;
      const trimmed = raw.trim();
      return trimmed.length > 0 ? trimmed : undefined;
    })(),
    participantClinicId: coerceNullableString(
      (item as { participant_clinic_id?: unknown }).participant_clinic_id,
    ),
    officialDiagnosis: coerceOfficialDiagnosis(
      (item as { official_diagnosis?: unknown }).official_diagnosis,
    ),
    hasVideo: Boolean((item as { has_video?: unknown }).has_video),
    captureMode: coerceNullableString((item as { capture_mode?: unknown }).capture_mode),
    qc: coerceCaptureQc((item as { qc?: unknown }).qc),
  }));
}

export async function fetchGazeResultDetail(id: number): Promise<GazeTestResultDetail> {
  const res = await authedFetch(`/gaze-test-results/${id}/`, { method: 'GET' });
  if (!res.ok) {
    const t = await res.text();
    throw new Error(`获取详情失败 (${res.status}) – ${t.slice(0, 200)}`);
  }
  const data = await res.json();
  if (!data || typeof data !== 'object') throw new Error('详情响应格式错误');
  const rawTasks: unknown[] = Array.isArray((data as { tasks?: unknown }).tasks)
    ? ((data as { tasks: unknown[] }).tasks ?? [])
    : [];
  const mappedTasks = rawTasks.map((task) => {
    const record = (task as Record<string, unknown>) ?? {};
    return {
      task: String(record.task ?? ''),
      label: String(record.label ?? ''),
      confidence: Number(record.confidence ?? 0),
      prob: typeof record.prob === 'number' ? record.prob : undefined,
      threshold: typeof record.threshold === 'number' ? record.threshold : undefined,
    };
  });
  const tasks = mappedTasks;

  let staticInfo: GazeTestResultDetail['static'] = null;
  const staticRaw = (data as { static?: unknown }).static;
  if (staticRaw && typeof staticRaw === 'object') {
    const raw = staticRaw as Record<string, unknown>;
    const ageValue = raw.age;
    const eduValue = raw.education;
    staticInfo = {
      name:
        typeof raw.name === 'string' && raw.name.trim().length > 0 ? raw.name.trim() : undefined,
      age:
        typeof ageValue === 'number'
          ? ageValue
          : Number.isFinite(Number(ageValue))
            ? Number(ageValue)
            : undefined,
      sex: typeof raw.sex === 'string' ? raw.sex : undefined,
      education:
        typeof eduValue === 'number'
          ? eduValue
          : Number.isFinite(Number(eduValue))
            ? Number(eduValue)
            : undefined,
    };
  }

  const timelineRaw = (data as { timeline?: unknown }).timeline;
  const timeline = Array.isArray(timelineRaw)
    ? (timelineRaw as unknown[])
        .map((seg) => {
          const record = (seg as Record<string, unknown>) ?? {};
          return {
            mediaIndex: Number(record.mediaIndex),
            start_ms: Number(record.start_ms),
            end_ms: Number(record.end_ms),
          } satisfies TimelineSeg;
        })
        .filter(
          (seg) =>
            Number.isFinite(seg.mediaIndex) &&
            Number.isFinite(seg.start_ms) &&
            Number.isFinite(seg.end_ms),
        )
    : null;

  return {
    id: Number((data as { id?: unknown }).id),
    status: String((data as { status?: unknown }).status ?? ''),
    name:
      coerceNullableString((data as { name?: unknown }).name) ??
      coerceParticipantSummary((data as { participant?: unknown }).participant)?.name ??
      staticInfo?.name ??
      undefined,
    label: String((data as { label?: unknown }).label ?? ''),
    confidence: coerceNullableNumber((data as { confidence?: unknown }).confidence),
    timestamp: String((data as { timestamp?: unknown }).timestamp ?? ''),
    participantClinicId:
      coerceParticipantSummary((data as { participant?: unknown }).participant)?.clinicParticipantId ??
      null,
    hasVideo: Boolean((data as { video?: unknown }).video),
    captureMode: coerceNullableString((data as { capture_mode?: unknown }).capture_mode),
    tasks,
    static: staticInfo,
    timeline,
    source: coerceNullableString((data as { source?: unknown }).source),
    meta:
      (data as { meta?: unknown }).meta && typeof (data as { meta?: unknown }).meta === 'object'
        ? ((data as { meta?: Record<string, unknown> }).meta ?? null)
        : null,
    participant: coerceParticipantSummary((data as { participant?: unknown }).participant),
    officialDiagnosis: coerceOfficialDiagnosis(
      (data as { official_diagnosis?: unknown }).official_diagnosis,
    ),
    video: coerceVideoArtifact((data as { video?: unknown }).video),
    qc: coerceCaptureQc((data as { qc?: unknown }).qc),
  } satisfies GazeTestResultDetail;
}

export async function submitForm(identifier: string): Promise<{ formDataId: number }> {
  const res = await authedFetch('/submit-form/', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ identifier }),
  });
  if (!res.ok) {
    const t = await res.text();
    throw new Error(`submit-form failed (${res.status}) – ${t.slice(0, 200)}`);
  }
  const data = await res.json();
  if (!data?.formDataId) throw new Error('submit-form 响应缺少 formDataId');
  return { formDataId: Number(data.formDataId) };
}

export async function finalizeChunkedUpload(
  formDataId: number,
  extra?: {
    static?: { name?: string; age: number; sex: 'M' | 'F' | string; education: number };
    timeline?: TimelineSeg[];
  },
): Promise<{ id: number; label: string; confidence: number; tasks?: TaskResult[] }> {
  const clientRunId = getOrCreateClientRunId();
  const payload: Record<string, unknown> = {
    formDataId,
    seq: -1,
    final: true,
    samples: [],
    clientRunId,
  };
  if (extra?.timeline) payload.timeline = extra.timeline;
  if (extra?.static) payload.static = extra.static;
  const body = gzipJson(payload);
  const res = await authedFetch('/submit-data-chunk/', {
    method: 'POST',
    headers: { 'Content-Type': 'application/gzip', 'Content-Encoding': 'gzip' },
    body,
    keepalive: true,
  });
  if (!res.ok) {
    const t = await res.text();
    throw new Error(`最终提交失败 (${res.status}) – ${t.slice(0, 200)}`);
  }
  const resp = await res.json();
  if (
    resp?.status !== 'success' ||
    typeof resp?.label !== 'string' ||
    typeof resp?.confidence !== 'number'
  ) {
    const msg = resp?.message || '最终提交返回了意外的负载';
    throw new Error(`最终提交错误：${msg}`);
  }
  const rawId =
    typeof resp.resultId === 'number'
      ? resp.resultId
      : typeof resp.id === 'number'
        ? resp.id
        : null;
  if (rawId === null) {
    throw new Error('最终提交响应缺少结果编号');
  }
  return {
    id: Number(rawId),
    label: resp.label,
    confidence: Number(resp.confidence),
    tasks: filterTasks(resp.tasks as TaskResult[] | undefined),
  };
}

export async function uploadSamplesSequential(
  formDataId: number,
  samples: EyeTraxSample[],
  opts: UploadOpts,
  extra?: ExtraMeta,
): Promise<{ id: number; label: string; confidence: number; tasks?: TaskResult[] }> {
  const total = samples.length;
  if (total === 0) throw new Error('没有可上传的样本');
  const clientRunId = getOrCreateClientRunId();

  let i = 0;
  let chunkSize = Math.max(4, Math.floor(opts.seedChunkSize || 80));
  let sent = 0;
  let estTotalChunks = Math.ceil(total / chunkSize);

  async function postChunk(
    seq: number,
    final: boolean,
    chunk: EyeTraxSample[],
    preNormalized?: UploadSamplePayload[],
  ) {
    const samplesPayload = preNormalized ?? normalizeSamplesForUpload(chunk);
    const payload: Record<string, unknown> = {
      formDataId,
      seq,
      final,
      clientRunId,
      samples: samplesPayload,
    };
    if (final && extra?.timeline) payload.timeline = extra.timeline;
    if (final && extra?.static) payload.static = extra.static;
    const body = gzipJson(payload);
    const res = await authedFetch('/submit-data-chunk/', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/gzip',
        'Content-Encoding': 'gzip',
      },
      body,
    });
    if (!res.ok) {
      const t = await res.text();
      throw new Error(`提交分片失败 (${res.status}) – ${t.slice(0, 200)}`);
    }
    return res.json();
  }

  let seq = 0;
  while (i < total) {
    let chunk = samples.slice(i, Math.min(total, i + chunkSize));
    let normalizedChunk = normalizeSamplesForUpload(chunk);
    let gz = gzipJson({ formDataId, seq, final: false, clientRunId, samples: normalizedChunk });
    while (gz.byteLength > opts.targetMaxBytes && chunk.length > 4) {
      chunkSize = Math.max(4, Math.floor(chunkSize * 0.7));
      chunk = samples.slice(i, Math.min(total, i + chunkSize));
      normalizedChunk = normalizeSamplesForUpload(chunk);
      gz = gzipJson({ formDataId, seq, final: false, clientRunId, samples: normalizedChunk });
      estTotalChunks = Math.ceil((total - i) / Math.max(1, chunkSize));
    }

    const final = i + chunk.length >= total;
    const resp = await postChunk(seq, final, chunk, normalizedChunk);
    sent++;
    opts.onProgress?.({ sentChunks: sent, totalChunks: estTotalChunks, bytes: gz.byteLength });
    if (final) {
      if (
        resp?.status !== 'success' ||
        typeof resp?.label !== 'string' ||
        typeof resp?.confidence !== 'number'
      ) {
        const msg = resp?.message || '最终提交返回了意外的负载';
        throw new Error(`最终提交错误：${msg}`);
      }
      const rawId =
        typeof resp.resultId === 'number'
          ? resp.resultId
          : typeof resp.id === 'number'
            ? resp.id
            : null;
      if (rawId === null) {
        throw new Error('最终提交响应缺少结果编号');
      }
      return {
        id: Number(rawId),
        label: resp.label,
        confidence: Number(resp.confidence),
        tasks: filterTasks(resp.tasks as TaskResult[] | undefined),
      };
    }
    i += chunk.length;
    seq += 1;
  }
  throw new Error('上传异常终止');
}

export async function submitChunk(
  formDataId: number,
  samples: UploadSampleInput[],
  seq?: number,
): Promise<void> {
  const normalized = normalizeSamplesForUpload(samples);
  const payload: Record<string, unknown> = {
    formDataId,
    final: false,
    clientRunId: getOrCreateClientRunId(),
    samples: normalized,
  };
  if (Number.isFinite(seq)) payload.seq = Number(seq);
  const body = gzipJson(payload);
  const res = await authedFetch('/submit-data-chunk/', {
    method: 'POST',
    headers: { 'Content-Type': 'application/gzip', 'Content-Encoding': 'gzip' },
    body,
  });
  if (!res.ok) {
    const t = await res.text();
    throw new Error(`提交分片失败 (${res.status}) – ${t.slice(0, 200)}`);
  }
}

export async function submitVideoSession(
  payload: ArgusCollectionDraftV1,
  video: Blob,
  extra?: {
    timeline?: TimelineSeg[];
    meta?: Record<string, unknown>;
    onProgress?: (info: VideoSubmitProgress) => void;
  },
): Promise<{
  id: number;
  state: string;
  label?: string;
  confidence?: number | null;
  tasks?: TaskResult[];
  participant?: ArgusParticipantSummary | null;
  officialDiagnosis?: ArgusOfficialDiagnosisSummary | null;
  video?: ArgusVideoArtifactSummary | null;
  qc?: ArgusCaptureQcV1 | null;
}> {
  const clientRunId = getOrCreateClientRunId();
  const meta = {
    ...(extra?.meta ?? {}),
    clientRunId,
  };
  const formData = new FormData();
  formData.append(
    'payload',
    JSON.stringify({
      clientRunId,
      participant: payload.participant,
      officialDiagnosis: payload.officialDiagnosis,
      intake: payload.intake,
      timeline: extra?.timeline,
      meta,
    }),
  );
  const extension = video.type.includes('mp4') ? 'mp4' : 'webm';
  formData.append('video', video, `argus-session.${extension}`);

  const res = await authedFormUpload('/submit-video-session/', formData, extra?.onProgress);
  if (res.status < 200 || res.status >= 300) {
    throw new Error(formatUploadError(res.status, res.text));
  }
  const resp = JSON.parse(res.text);
  const rawId = typeof resp.resultId === 'number' ? resp.resultId : typeof resp.id === 'number' ? resp.id : null;
  if (rawId === null) {
    throw new Error('视频提交响应缺少结果编号');
  }
  return {
    id: Number(rawId),
    state: String(resp?.state ?? ''),
    label: coerceNullableString(resp?.label) ?? undefined,
    confidence: coerceNullableNumber(resp?.confidence),
    tasks: filterTasks(
      Array.isArray(resp?.tasks)
        ? (resp.tasks as Array<Record<string, unknown>>).map((task) => ({
            task: String(task.task ?? ''),
            label: String(task.label ?? ''),
            confidence: Number(task.confidence ?? 0),
            prob: typeof task.prob === 'number' ? task.prob : undefined,
            threshold: typeof task.threshold === 'number' ? task.threshold : undefined,
          }))
        : undefined,
    ),
    participant: coerceParticipantSummary(resp?.participant),
    officialDiagnosis: coerceOfficialDiagnosis(resp?.officialDiagnosis),
    video: coerceVideoArtifact(resp?.video),
    qc: coerceCaptureQc(resp?.qc),
  };
}

export async function downloadDatasetExport(format: 'json' | 'csv' = 'json'): Promise<void> {
  const res = await authedFetch(`/dataset-export/?format=${format}`, { method: 'GET' });
  if (!res.ok) {
    const t = await res.text();
    throw new Error(`导出数据清单失败 (${res.status}) – ${t.slice(0, 200)}`);
  }
  await downloadResponseToFile(
    res,
    format === 'csv' ? 'argus-dataset-export.csv' : 'argus-dataset-export.json',
  );
}

export async function downloadResultVideo(id: number): Promise<void> {
  const res = await authedFetch(`/gaze-test-results/${id}/video/`, { method: 'GET' });
  if (!res.ok) {
    const t = await res.text();
    throw new Error(`下载原始视频失败 (${res.status}) – ${t.slice(0, 200)}`);
  }
  await downloadResponseToFile(res, `argus-session-${id}.webm`);
}
